/**
 * Sprint 9 refactor — three small sub-components extracted from
 * DeploymentDetail.jsx to keep the main page file under 500 lines.
 *
 * No behavior change — these are verbatim relocations.
 */
import React, { useEffect, useState } from "react";

import { useMarkedItems } from "../../lib/markedItems";
import { api } from "../../lib/api";


export function MarkedHeaderBadge() {
  const { marks, open } = useMarkedItems();
  return (
    <button
      onClick={open}
      className="btn-quiet"
      style={{
        fontFamily: "Oswald",
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        fontSize: "11px",
        color: marks.length > 0 ? "#1A1A1A" : "#6F6F6F",
        border: `1px solid ${marks.length > 0 ? "#1A1A1A" : "#999898"}`,
        padding: "8px 14px",
      }}
      data-testid="marked-tray-open"
      aria-label={`Marked items (${marks.length})`}
    >
      Marked ({marks.length})
    </button>
  );
}


/**
 * Per-deployment toggle for `individual_reports_enabled`. When ON, every
 * respondent who completes the survey can read their own report at
 * `/respondent-report/:token`. When OFF, the same URL returns 403 with
 * the message *"Individual reflections are not available for this wave."*
 *
 * Position: under the metadata strip in the deployment header. Quiet
 * by default — this is not a primary action.
 */
export function IndividualReportsToggle({ deploymentId, initial, canEdit }) {
  const [enabled, setEnabled] = useState(Boolean(initial));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // sync if the underlying deployment changes (e.g., refetch on tab change).
  // setState wrapped in an async IIFE to defer it past the effect's
  // synchronous body (react-hooks/set-state-in-effect).
  useEffect(() => { (async () => { setEnabled(Boolean(initial)); })(); }, [initial]);

  async function flip() {
    if (busy) return;
    const next = !enabled;
    setBusy(true);
    setError("");
    // optimistic update
    setEnabled(next);
    try {
      await api.patch(
        `/api/deployments/${deploymentId}/settings`,
        { individual_reports_enabled: next },
      );
    } catch (e) {
      setEnabled(!next);
      setError(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 flex items-center gap-3" data-testid="individual-reports-toggle">
      <button
        onClick={canEdit ? flip : undefined}
        disabled={!canEdit || busy}
        aria-pressed={enabled}
        title={canEdit ? "Toggle whether respondents can read their own reflection at /respondent-report/<token>" : "Read-only"}
        data-testid="individual-reports-toggle-button"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "10px",
          padding: "6px 12px",
          fontFamily: "Oswald",
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          fontSize: "10px",
          color: enabled ? "#1A1A1A" : "#888",
          background: "transparent",
          border: `1px solid ${enabled ? "#1A1A1A" : "#DDDDDD"}`,
          cursor: canEdit ? (busy ? "wait" : "pointer") : "default",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: "inline-block",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: enabled ? "#4592A8" : "#CCCCCC",
          }}
        />
        Individual reports: {enabled ? "on" : "off"}
      </button>
      {error && <span className="text-xs" style={{ color: "#AD393E" }}>{error}</span>}
    </div>
  );
}


export function FilteredCohortCard({ scores, filters }) {
  const filterText = Object.entries(filters)
    .map(([k, v]) => `${k}: ${v}`)
    .join(" · ");
  return (
    <div className="border-t border-gray-100 pt-8" data-testid="filtered-cohort">
      <div className="eyebrow mb-1">Filtered cohort</div>
      <h3 className="text-base mb-4" style={{ fontFamily: "Inter", fontWeight: 500 }}>
        {filterText || "All respondents"} · n = {scores.n}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {scores.dimensions.map((d) => (
          <div key={d.dimension} className="border-l-2 pl-3 py-1" style={{ borderColor: "#4592A8" }}>
            <div className="text-xs uppercase tracking-wider text-gray-500" style={{ fontFamily: "Oswald" }}>
              {d.dimension.replace("_", " ")}
            </div>
            <div className="text-2xl text-ink" style={{ fontFamily: "Oswald", fontWeight: 700 }}>
              {d.mean.toFixed(2)}
            </div>
            <div className="text-xs text-gray-500">{d.rubric_level}</div>
          </div>
        ))}
      </div>
    </div>
  );
}



/**
 * Raw data export button — downloads a .xlsx with raw responses,
 * dimension summary, and reflections for one ORI deployment. Mirrors
 * the pre-platform consultant workbook so downstream analysis in
 * Excel picks up where the platform ends.
 *
 * Only renders for organizational deployments with at least one
 * completed respondent — the endpoint returns 409 otherwise, and
 * showing a disabled button reads clearer than a click→error.
 */
export function RawExportButton({ deploymentId, completedCount, deploymentMode }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (deploymentMode === "individual") return null;
  const disabled = busy || !completedCount;

  async function doExport() {
    if (disabled) return;
    setBusy(true);
    setError("");
    try {
      const res = await api.get(
        `/api/deployments/${deploymentId}/raw-export`,
        { responseType: "blob" },
      );
      // Extract filename from Content-Disposition; fallback to a sane default.
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(cd);
      const fname = match ? match[1] : "ORI_raw.xlsx";
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Export failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={doExport}
        disabled={disabled}
        data-testid="raw-export-button"
        title={
          !completedCount
            ? "No completed respondents yet — nothing to export."
            : "Download raw responses, dimension summary, and reflections as .xlsx"
        }
        style={{
          fontFamily: "Oswald",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          fontSize: "11px",
          color: disabled ? "#999898" : "#2F6B7C",
          border: `1px solid ${disabled ? "#D0D0D0" : "#4592A8"}`,
          background: "transparent",
          padding: "8px 14px",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        {busy ? "Preparing…" : "Export raw data"}
      </button>
      {error && (
        <span className="text-xs" style={{ color: "#AD393E" }} data-testid="raw-export-error">
          {error}
        </span>
      )}
    </div>
  );
}
